import type { Collection as TypedCollection } from 'utils/create-collection/types';
import type { Board } from 'chessboard/model/board/types'; // Must be relative path otherwise test generator breaks
import type { TimeControl } from 'chessboard/api/time-control/config/types';
import type JCE from '@chesscom/js-chess-engine';
import type { EffectTypes } from 'chessboard/plugins/effects/config/types';
import type { KeyedHighlight } from 'chessboard/plugins/highlights/config/types';
import type { AntiCheat } from 'chessboard/plugins/fps/config/types';

export namespace Game {
  export enum ColorsAsNumbers {
    White = 1,
    Black = 2,
  }

  export type VariantString = JCE.GameTypes;

  export enum Variants {
    Bughouse = 'bughouse',
    Chess = 'chess',
    Chess960 = 'chess960',
    Crazyhouse = 'crazyhouse',
    KingOfTheHill = 'kingofthehill',
    ThreeCheck = 'threecheck',
  }

  export const JCEVariantTypes = {
    [Variants.Bughouse]: 'Bughouse',
    [Variants.Chess]: 'Chess',
    [Variants.Chess960]: 'Chess960',
    [Variants.Crazyhouse]: 'Crazyhouse',
    [Variants.ThreeCheck]: '3-check',
    [Variants.KingOfTheHill]: 'King of the Hill',
  };

  export type GameTypes = Variants | TimeControl.Types;

  export enum ColorsAsLetters {
    White = 'w',
    Black = 'b',
  }

  export enum ColorsAsWords {
    White = 'white',
    Black = 'black',
  }

  export interface EachColor<T = any> {
    [ColorsAsWords.Black]: T;
    [ColorsAsWords.White]: T;
  }

  export type Color = ColorsAsNumbers.White | ColorsAsNumbers.Black;

  export const DefaultTimeControl = 5;

  export enum EmptyPGNHeaders {
    Black = '?',
    Event = '?',
    Date = '????.??.??',
    Site = '?',
    Value = '?',
    Result = '*',
    Round = '?',
    White = '?',
  }

  export type Line = JCE.Line;

  export namespace Move {
    export type FormattedMove = Model | Attempt | DropAttempt;

    export interface Base {
      isExternalBoard?: boolean;
      animate?: boolean;
      time?: number;
      userGenerated?: boolean;
      userGeneratedDrop?: boolean;
      // ac is an obfuscated alias for Anti-Cheat
      ac?: AntiCheat.CheatState;
    }

    export interface Model extends Base, JCE.Move.Model {}

    export interface Attempt extends Base, JCE.Move.Attempt {}

    export interface DropAttempt extends Base, JCE.Move.DropAttempt {
      color?: Game.ColorsAsNumbers;
    }

    export interface Extended extends Model {
      animate?: boolean;
      EPCapturedSquare?: Board.SanSquare;
      kingTo?: Board.SanSquare;
      rookMove?: {
        animate?: boolean;
        from: Board.SanSquare;
        to: Board.SanSquare;
      };
    }

    /**
     * minimized model for convenience in API. Can't be directly passed to JCE before formatting.
     */
    export interface SanModel extends Base {
      san: string;
    }

    /**
     * minimized model for convenience in API. Can't be directly passed to JCE before formatting.
     */
    export interface TcnModel extends Base {
      tcn: string;
    }

    export interface Node extends JCE.Node.Model {
      animate?: boolean;
      customColor?: string;
      isCommentEnabled?: boolean;
      wholeMoveNumber?: number;
      ply?: number;
      timestamp?: number;
      userGenerated?: boolean;
      // ac is an obfuscation of Anti-Cheat
      ac?: AntiCheat.CheatState;
    }

    export interface ExtendedNode extends Node {
      animate?: boolean;
      EPCapturedSquare?: Board.SanSquare;
      rookMove?: {
        animate?: boolean;
        from: Board.SanSquare;
        to: Board.SanSquare;
      };
    }

    export enum Flags {
      Capture = 1,
      BigPawn = 2,
      EPCapture = 4,
      AnyCapture = 5,
      Promotion = 8,
      CapturePromotion = 9,
      KingsideCastle = 16,
      QueensideCastle = 32,
      AnyCastle = 48,
      Drop = 64,
      DropOrCapture = 69,
      DropOrPromote = 72,
    }

    export interface Undo {
      animate?: boolean;
      from: Board.SanSquare;
      to: Board.SanSquare;
    }

    export enum Types {
      Capture,
      Castle,
      Check,
      Checkmate,
      Normal,
      Premove,
      Promotion,
    }
  }

  export namespace Node {
    export interface Model {
      line: number;
      move: number;
    }

    export namespace Api {
      export namespace Marking {
        export interface Model {
          [Game.Node.Marking.Types.Arrow]?: ApiModel<Node.Marking.Arrow.Model>;
          [Game.Node.Marking.Types.CustomItem]?: ApiModel<Node.Marking.CustomItem.Model>;
          [Game.Node.Marking.Types.Effect]?: ApiModel<Node.Marking.Effect.Model>;
          [Game.Node.Marking.Types.Square]?: ApiModel<Node.Marking.Square.Model>;
        }

        export type ApiModel<T> = T | T[];
      }
    }

    export namespace Marking {
      export type Model = Extended & (Arrow.Model | CustomItem.Model | Effect.Model | Square.Model);

      export type Color = string | ColorKeys;

      export interface Collection {
        [Game.Node.Marking.Types.Arrow]: Arrow.Collection;
        [Game.Node.Marking.Types.CustomItem]: CustomItem.Collection;
        [Game.Node.Marking.Types.Effect]: Effect.Collection;
        [Game.Node.Marking.Types.Square]: Square.Collection;
      }

      export interface Base {
        color?: Marking.Color;
        opacity?: number;
      }

      export interface Extended {
        key: Board.SanSquare | string;
        markingType: Game.Node.Marking.Types;
      }

      export interface ImageOptions {
        percentOfSquare?: number;
        url: string;
      }

      export interface TextOptions {
        color?: string;
        value: string;
      }

      export namespace Categories {
        export type All = Arrow.Model | CustomItem.Model | Effect.Model | Square.Model;
        export type CollectionContainingJCEMarkings<T> = ExtendedModel<T> | JCE.Marking.Model;
        export type WithMoveLan = Arrow.Model;
        export type WithSquare = Exclude<All, Arrow.Model>;
        export type WithType = CustomItem.Model | Effect.Model;
      }

      export type ExtendedModel<T> = Extended & T;

      export namespace Arrow {
        export interface Model extends Base {
          from: Board.SanSquare;
          to: Board.SanSquare;
        }

        export interface Collection {
          [key: string]: ExtendedModel<Model>;
        }
      }

      export namespace CustomItem {
        export interface Model extends Base {
          image?: ImageOptions;
          square: Board.SanSquare;
          text?: TextOptions;
          type: Types;
          visualOrder?: VisualOrder.Model;
          frame?: Frame;
          removalClass?: string;
          removalSound?: string;
          removalTimeout?: number;
        }

        export interface Frame {
          imageURL: string;
          imageAnimated?: boolean;
          positionRatioX: number;
          positionRatioY: number;
          widthRatio: number;
          heightRatio: number;
        }

        export namespace VisualOrder {
          export interface Model {
            position?: VisualOrder.Positions;
            target: VisualOrder.Targets;
          }

          export const enum Targets {
            Arrows = 'arrows',
            Effects = 'effects',
            Pieces = 'pieces',
          }

          export const enum Positions {
            Above = 'above',
            Below = 'below',
          }
        }

        export interface Collection {
          [key: string]: ExtendedModel<Model>;
        }

        export enum Types {
          Image = 'Image',
          Star = 'Star',
          TextBox = 'TextBox',
        }
      }

      export namespace Effect {
        export interface Model extends Base {
          square: Board.SanSquare;
          type: Types;
        }

        export interface Collection {
          [key: string]: ExtendedModel<Model>;
        }

        export type Types = EffectTypes;
      }

      export namespace Square {
        export interface Model extends Base {
          square: Board.SanSquare;
        }

        export interface Collection {
          [key: string]: ExtendedModel<Model>;
        }
      }

      // these types will change once we can change them in JCE
      export enum ColorKeys {
        alt = 'a',
        custom = 'c',
        default = 'd',
        shift = 's',
        ctrl = 'l',
      }

      export enum JCEColorsMap {
        a = 'alt',
        c = 'custom',
        d = 'default',
        s = 'shift',
        l = 'ctrl',
      }

      /* eslint-disable no-shadow */
      export enum Types {
        Arrow = 'arrow',
        Effect = 'effect',
        Square = 'square',
        CustomItem = 'customItem',
      }
    }
  }

  export type Threats = JCE.Threats;

  export namespace Piece {
    export interface Model extends JCE.Piece.Model {
      file?: number;
      rank?: number;
      square?: Board.SanSquare;
    }

    export interface Concise {
      color: Game.ColorsAsNumbers;
      type: Types;
    }

    export type ConciseObject = {
      [key: string]: Concise;
    };

    export type Collection = TypedCollection<Model, Board.SanSquare>;

    export interface DraggingPiece extends Model {
      coords: Board.Coords;
    }

    export enum Names {
      p = 'pawn',
      n = 'knight',
      b = 'bishop',
      r = 'rook',
      q = 'queen',
      k = 'king',
    }

    export namespace ShortStrings {
      export type Black = 'p' | 'n' | 'b' | 'r' | 'q' | 'k';
      export type White = 'P' | 'N' | 'B' | 'R' | 'Q' | 'K';
    }

    export type ShortString = ShortStrings.Black | ShortStrings.White;

    export enum Types {
      Pawn = 'p',
      Knight = 'n',
      Bishop = 'b',
      Rook = 'r',
      Queen = 'q',
      King = 'k',
    }
  }

  export namespace Item {
    export interface Model {
      square: Board.SanSquare;
    }

    export type Collection = TypedCollection<Model, Board.SanSquare>;
  }

  export namespace Highlight {
    export interface Model {
      color?: string;
      opacity: number;
      square: Board.SanSquare;
      turn?: ColorsAsNumbers;
      type?: Highlight.Types;
    }

    export type Collection = TypedCollection<KeyedHighlight, Board.SanSquare>;

    export enum Defaults {
      Color = '#ffff00',
      Opacity = 0.5,
    }

    export namespace Blinking {
      export interface Model extends Highlight.Model {
        interval: number;
        times: number;
      }

      export type Collection = TypedCollection<Model, Board.SanSquare>;
    }

    export enum Types {
      Analysis = 'analysis',
      Move = 'move',
      Premove = 'premove',
      Selection = 'selection',
    }
  }

  export enum TimeControlTypes {
    Blitz = 'blitz',
    Bullet = 'bullet',
    Rapid = 'rapid',
  }

  export enum Results {
    Black = '0-1',
    Draw = '1/2-1/2',
    None = '*',
    White = '1-0',
  }

  export namespace SquareIcon {
    export interface Model {
      position: Board.Coords;
      square: Board.SanSquare;
      template: string;
    }

    export type Collection = TypedCollection<Model, Board.SanSquare>;
  }

  export interface TimeControl {
    baseTime: number;
    increment: number | undefined;
  }
}
